import { useMemo, useState } from "react";
import PageHeader from "@/components/feature/PageHeader";
import PlotCard from "@/components/base/PlotCard";
import EmptyState from "@/components/base/EmptyState";
import { getAllPlots } from "@/lib/content";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const PAGE_STEP = 3;

export default function PlotCategoryPage() {
  const allPlots = getAllPlots();
  const tags = useMemo(() => {
    const set = new Set<string>();
    allPlots.forEach((p) => p.tags.forEach((t) => set.add(t)));
    return Array.from(set);
  }, [allPlots]);

  const [activeTag, setActiveTag] = useState("全部");
  const [visible, setVisible] = useState(PAGE_STEP);

  const filtered = useMemo(() => {
    const list = [...allPlots].sort((a, b) => (a.date < b.date ? 1 : -1));
    if (activeTag === "全部") return list;
    return list.filter((p) => p.tags.includes(activeTag));
  }, [allPlots, activeTag]);

  const visibleItems = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  const handleTag = (tag: string) => {
    setActiveTag(tag);
    setVisible(PAGE_STEP);
  };

  useDocumentTitle(
    "剧情解析 - 黑皮辣妹",
    "黑皮辣妹影视剧情介绍频道，提供热门影视剧情解析、结局拆解与人物深度分析，读懂每一个好故事。",
  );

  return (
    <div className="hp-fade-up">
      <PageHeader
        title="热门影视剧情解析"
        description="从结局拆解到伏笔盘点，从人物深读到设定梳理，用深入浅出的方式带你看懂每一个好故事。"
        breadcrumb={[{ label: "剧情解析" }]}
        eyebrow="Plot Analysis"
      />

      <section className="w-full px-4 md:px-6">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <i className="ri-price-tag-3-line text-secondary-500" />
            <span className="text-sm font-medium text-foreground-800">按类型筛选</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {["全部", ...tags].map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => handleTag(tag)}
                className={`px-3.5 py-1.5 rounded-md text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  activeTag === tag
                    ? "bg-primary-500 text-background-50"
                    : "bg-secondary-100 text-secondary-900 hover:bg-accent-100 hover:text-accent-900"
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-foreground-500">
            共 <span className="text-primary-600 font-medium">{filtered.length}</span> 篇解析
          </p>
        </div>

        {visibleItems.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {visibleItems.map((item) => (
                <PlotCard key={item.id} item={item} />
              ))}
            </div>
            {hasMore ? (
              <div className="flex justify-center mt-8">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + PAGE_STEP)}
                  className="inline-flex items-center gap-2 border border-background-300 bg-background-50 text-foreground-800 text-sm font-medium px-6 py-3 rounded-md hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-line" />
                  加载更多
                </button>
              </div>
            ) : (
              <p className="text-center text-sm text-foreground-400 mt-8">已经到底啦</p>
            )}
          </>
        ) : (
          <EmptyState
            title="暂无相关剧情解析"
            description="当前标签下还没有解析内容，试试切换其他类型标签。"
            actionLabel="查看全部解析"
            onAction={() => handleTag("全部")}
          />
        )}
      </section>
    </div>
  );
}